import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthFrame from "@/components/AuthFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DEFERRED_MESSAGE } from "@/lib/offlineTransport";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { confirmPasswordReset, validatePasswordReset } = useAuth();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const linkComplete = useMemo(() => Boolean(email && token), [email, token]);
  const [linkStatus, setLinkStatus] = useState(linkComplete ? "checking" : "missing");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!linkComplete) {
      setLinkStatus("missing");
      return undefined;
    }
    let active = true;
    setLinkStatus("checking");
    validatePasswordReset({ email, token })
      .then(() => { if (active) setLinkStatus("valid"); })
      .catch((error) => { if (active) setLinkStatus(error?.response?.data?.detail === DEFERRED_MESSAGE ? "offline" : "invalid"); });
    return () => { active = false; };
  }, [email, linkComplete, token, validatePasswordReset]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("De wachtwoorden zijn niet gelijk");
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset({ email, token, password: form.password });
      toast.success("Je wachtwoord is gewijzigd");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Deze resetlink is ongeldig of verlopen");
    } finally {
      setLoading(false);
    }
  };

  if (linkStatus === "missing") {
    return (
      <AuthFrame title="Ongeldige resetlink" subtitle="Deze link mist gegevens of is niet compleet." footer={<Link to="/forgot-password" className="font-semibold text-amber-700">Nieuwe link aanvragen</Link>}>
        <Button asChild className="w-full h-[52px] bg-amber-600 hover:bg-amber-700"><Link to="/forgot-password">Nieuwe link aanvragen</Link></Button>
      </AuthFrame>
    );
  }

  if (linkStatus === "checking") {
    return (
      <AuthFrame title="Resetlink controleren" subtitle="Een moment, we controleren of deze beveiligde link nog geldig is.">
        <Button disabled className="w-full h-[52px] bg-amber-600">Controleren...</Button>
      </AuthFrame>
    );
  }

  if (linkStatus === "invalid") {
    return (
      <AuthFrame title="Resetlink verlopen" subtitle="Deze link is gebruikt, vervangen of ouder dan 30 minuten." footer={<Link to="/forgot-password" className="font-semibold text-amber-700">Nieuwe link aanvragen</Link>}>
        <Button asChild className="w-full h-[52px] bg-amber-600 hover:bg-amber-700"><Link to="/forgot-password">Nieuwe link aanvragen</Link></Button>
      </AuthFrame>
    );
  }

  if (linkStatus === "offline") {
    return (
      <AuthFrame title="Geen internetverbinding" subtitle={DEFERRED_MESSAGE} footer={<Link to="/login" className="font-semibold text-amber-700">Terug naar inloggen</Link>}>
        <Button disabled className="w-full h-[52px] bg-amber-600">{DEFERRED_MESSAGE}</Button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Nieuw wachtwoord" subtitle="Kies een nieuw wachtwoord van minimaal 8 tekens." heroTitle="Veilig weer verder." heroText="Na het wijzigen kun je direct opnieuw inloggen." footer={<Link to="/login" className="font-semibold text-amber-700">Terug naar inloggen</Link>}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="new-password" className="text-sm font-semibold text-zinc-800">Nieuw wachtwoord</Label>
          <Input id="new-password" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} autoComplete="new-password" minLength={8} maxLength={128} required className="h-[52px] rounded-xl text-base" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm-password" className="text-sm font-semibold text-zinc-800">Herhaal wachtwoord</Label>
          <Input id="confirm-password" type="password" value={form.confirm} onChange={(event) => setForm({ ...form, confirm: event.target.value })} autoComplete="new-password" minLength={8} maxLength={128} required className="h-[52px] rounded-xl text-base" />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-[52px] rounded-xl text-base bg-amber-600 hover:bg-amber-700 font-bold">
          {loading ? "Bezig..." : "Wachtwoord wijzigen"}
        </Button>
      </form>
    </AuthFrame>
  );
}
